import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";

import LoadingIndicator from "../../../loading-indicator";

const ParagraphNewsletterRegistration = ({ content }) => {
  const intl = useIntl();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // The newsletter provider is called server-side (Node proxy -> Drupal), so
  // no API credentials ever reach the browser. Already registered addresses
  // get the same response as new ones, to not leak who is subscribed.
  const submitHandler = async (values, actions) => {
    setLoading(true);
    setError(false);

    try {
      await axios.post(
        "/api/newsletter/subscribe",
        {
          email: values.email,
          channel: content.fieldNewsletterChannel?.uuid,
          consent: values.consent,
          website: values.website,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setSuccess(true);
      actions.resetForm();
    } catch (e) {
      setError(e.response?.status === 429 ? "newsletter.rate_limit" : "newsletter.error");
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  const sectionClassNames = classNames({
    "paragraph paragraph-newsletter-registration": true,
    [`color-${content.fieldColorScheme || "basic"}`]: true,
  });

  return (
    <section
      className={sectionClassNames}
      data-darkmode={content.fieldDarkMode}
    >
      {loading && <LoadingIndicator />}

      <div className="container">
        <div className="row">
          {success ? (
            <div className="col-12 heading" role="status">
              <h2>
                <FormattedMessage id="newsletter.success" />
              </h2>
            </div>
          ) : (
            <>
              <div className="col-12 heading">
                {content.fieldHeading && <h2>{content.fieldHeading}</h2>}
              </div>
              <div className="col-12 form-part">
                <Formik
                  onSubmit={submitHandler}
                  validateOnChange={false}
                  validateOnBlur={false}
                  initialValues={{
                    email: "",
                    consent: false,
                    website: "",
                  }}
                  validationSchema={Yup.object().shape({
                    email: Yup.string()
                      .trim()
                      .max(254)
                      .email(
                        intl.formatMessage({ id: "newsletter.form.email_invalid" })
                      )
                      .required(
                        intl.formatMessage({ id: "newsletter.form.email_error" })
                      ),
                    consent: Yup.bool().oneOf(
                      [true],
                      intl.formatMessage({ id: "newsletter.form.consent_error" })
                    ),
                  })}
                >
                  {({ errors, touched }) => (
                    <Form noValidate role="form">
                      <div className="form-row">
                        <div className="col-12 form-group">
                          <label htmlFor="email">
                            <FormattedMessage id="newsletter.form.email_label" />
                          </label>
                          <div className="input-wrapper">
                            <Field
                              type="email"
                              name="email"
                              id="email"
                              className="form-control"
                              placeholder="E-Mail"
                              aria-describedby={errors.email ? "email-error" : undefined}
                              aria-invalid={errors.email ? "true" : "false"}
                            />
                            <button
                              type="submit"
                              className="submit-newsletter"
                            >
                              <FormattedMessage id="newsletter.form.submit" />
                            </button>
                          </div>
                          {errors.email && touched.email && (
                            <span
                              id="email-error"
                              className="form-error text-danger"
                              role="region"
                              aria-live="polite"
                            >
                              {errors.email}
                            </span>
                          )}
                          {error && (
                            <div
                              className="form-error text-danger"
                              role="region"
                              aria-live="polite"
                            >
                              <FormattedMessage id={error} />
                            </div>
                          )}
                        </div>

                        {/* Honeypot: hidden from humans and assistive tech,
                            filled by bots; the server drops such requests. */}
                        <div className="newsletter-hp" aria-hidden="true">
                          <label htmlFor="newsletter-website">Website</label>
                          <Field
                            type="text"
                            name="website"
                            id="newsletter-website"
                            tabIndex={-1}
                            autoComplete="off"
                          />
                        </div>

                        <div className="col-12 form-check">
                          <Field
                            type="checkbox"
                            name="consent"
                            id="consent"
                            className="form-check-input"
                            aria-describedby={
                              errors.consent && touched.consent
                                ? "consent-error"
                                : undefined
                            }
                            aria-invalid={
                              errors.consent && touched.consent ? "true" : "false"
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="consent"
                            id="form-check-label"
                          >
                            <FormattedMessage id="newsletter.form.consent_label" />
                          </label>
                        </div>

                        <div className="col-12">
                          {errors.consent && touched.consent && (
                            <span
                              id="consent-error"
                              className="form-error text-danger"
                              role="region"
                              aria-live="polite"
                            >
                              {errors.consent}
                            </span>
                          )}
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

ParagraphNewsletterRegistration.propTypes = {
  content: PropTypes.shape({
    fieldHeading: PropTypes.string,
    fieldText: PropTypes.string,
    fieldColorScheme: PropTypes.string,
    fieldDarkMode: PropTypes.bool,
    fieldNewsletterChannel: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
};

export default ParagraphNewsletterRegistration;